/**
 * Whether a touch landed inside something that scrolls sideways of its own accord,
 * anywhere between the target and the element handling the gesture.
 *
 * A horizontal drag gesture and a horizontal scroll are the same movement, so whichever
 * one is nearer the finger has to win — which is always the scroller, because scrolling
 * something is a direct manipulation of it and the gesture is a shortcut. Every native
 * list does this with a nested scroller.
 *
 * There is a lot of scrollable width in this app for a swipe to trip over: the financial
 * tables scroll horizontally by design, and Arc's markdown replies put an
 * `overflow-x: auto` box around every code block and wide table.
 *
 * Only a scroller with somewhere left to go counts. A code block whose content fits has
 * the overflow style and nothing to scroll, and stealing the gesture from it would mean
 * the swipe silently stopped working over half the messages in a channel.
 */
export const startsInHorizontalScroller = (
  target: EventTarget | null,
  boundary: HTMLElement,
): boolean => {
  let node = target instanceof Element ? target : null
  while (node && node !== boundary) {
    if (node.scrollWidth > node.clientWidth) {
      const { overflowX } = window.getComputedStyle(node)
      if (overflowX === 'auto' || overflowX === 'scroll') return true
    }
    node = node.parentElement
  }
  return false
}

/** A drag ends with a synthesised click on whatever it was released over; swallow that one. */
export const suppressNextClick = () => {
  const handler = (event: MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
  }
  document.addEventListener('click', handler, { capture: true, once: true })
  setTimeout(() => document.removeEventListener('click', handler, { capture: true }), 0)
}
